import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Sparkles, Plus, Trash2, FileText, Loader2, MessageCircle, Inbox, Receipt } from "lucide-react";
import { toast } from "sonner";
import ProjectChat from "@/components/ProjectChat";
import ProjectAISidebar from "@/components/ProjectAISidebar";
import SimilarPracticesPanel from "@/components/SimilarPracticesPanel";
import Fatturazione from "@/pages/Fatturazione";

const STATUSES = ["bozza", "aperta", "in_lavorazione", "attesa_cliente", "revisione", "chiusa", "archiviata"];

const prioColors = { bassa:"bg-slate-100 text-slate-600", normale:"bg-blue-50 text-blue-700", alta:"bg-orange-100 text-orange-700", urgente:"bg-red-100 text-red-700" };
const typeLabel = { informativa:"Informativa", richiesta_decisione:"Richiesta decisione", richiesta_integrazione:"Richiesta integrazione", sollecito:"Sollecito", delega:"Delega" };

export default function PraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [memos, setMemos] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [newNote, setNewNote] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);

  const load = async () => {
    const [pr, tr, nr, dr, ar] = await Promise.all([
      api.get(`/practices/${id}`),
      api.get(`/tasks?project_id=${id}`),
      api.get(`/notes?project_id=${id}`),
      api.get(`/documents?project_id=${id}`),
      api.get(`/activity?limit=100`),
    ]);
    setP(pr.data); setTasks(tr.data); setNotes(nr.data); setDocs(dr.data);
    setActivity(ar.data.filter(a => a.entity_id === id));
    try { const m = await api.get(`/memos/inbox?project_id=${id}`); setMemos(m.data); } catch { setMemos([]); }
  };

  useEffect(() => { load(); }, [id]);
  if (!p) return <div className="text-sm text-slate-500">Caricamento...</div>;

  const updateField = async (patch) => {
    await api.patch(`/practices/${id}`, patch);
    toast.success("Aggiornato");
    load();
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    await api.post("/tasks", { project_id: id, title: newTask });
    setNewTask(""); load();
  };
  const toggleTask = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "completata" ? "aperta" : "completata" });
    load();
  };
  const removeTask = async (tid) => { await api.delete(`/tasks/${tid}`); load(); };
  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post("/notes", { project_id: id, content: newNote });
    setNewNote(""); load();
  };
  const generateSummary = async () => {
    setSummaryLoading(true);
    try { const { data } = await api.post(`/ai/summarize-practice/${id}`); setSummary(data.summary); }
    catch { toast.error("Errore AI"); }
    finally { setSummaryLoading(false); }
  };

  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" onClick={()=>navigate("/pratiche")} className="text-slate-600" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Pratiche
      </Button>

      {/* Header progetto */}
      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-slate-500">{p.numero_protocollo}</div>
            <h1 className="text-2xl font-heading font-bold text-navy-900 mt-1">{p.title}</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-slate-600 flex-wrap">
              <span>Cliente: <strong className="text-navy-900">{p.client?.name || "—"}</strong></span>
              <span className="text-slate-300">·</span>
              <span className="capitalize">{p.type}</span>
              <span className="text-slate-300">·</span>
              <span>Scadenza: <strong className="text-navy-900">{p.deadline || "—"}</strong></span>
              {p.capoprogetto && <><span className="text-slate-300">·</span><span>Capoprogetto: <strong className="text-navy-900">{p.capoprogetto.full_name}</strong></span></>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${prioColors[p.priority]} border-0`}>{p.priority}</Badge>
            <Select value={p.status} onValueChange={(v)=>updateField({ status: v })}>
              <SelectTrigger className="w-44" data-testid="status-select"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* DNA progetto */}
      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Compenso previsto</div>
          <div className="text-xl font-heading font-bold text-navy-900 mt-1">€ {(p.compenso_previsto || 0).toLocaleString("it-IT")}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Avanzamento</div>
          <div className="text-xl font-heading font-bold text-navy-900 mt-1">{p.progress_pct}%</div>
          <Slider min={0} max={100} step={5} value={[p.progress_pct]} onValueCommit={(v) => updateField({ progress_pct: v[0] })} className="mt-2" data-testid="progress-slider" />
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Team</div>
          <div className="text-xl font-heading font-bold text-navy-900 mt-1">{p.assignees_detail?.length || 0} persone</div>
          <div className="flex -space-x-1.5 mt-2">
            {p.assignees_detail?.slice(0,5).map(u => (
              <div key={u.id} title={u.full_name} className="w-6 h-6 rounded-full bg-navy-800 text-white text-[10px] flex items-center justify-center border-2 border-white">{u.full_name.split(" ").map(s=>s[0]).slice(0,2).join("")}</div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Apertura</div>
          <div className="text-xl font-heading font-bold text-navy-900 mt-1">{p.open_date ? new Date(p.open_date).toLocaleDateString("it-IT") : "—"}</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Tabs defaultValue="riepilogo">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="riepilogo" data-testid="tab-summary">Riepilogo</TabsTrigger>
              <TabsTrigger value="task" data-testid="tab-tasks">Task ({tasks.length})</TabsTrigger>
              <TabsTrigger value="documenti" data-testid="tab-docs">Documenti ({docs.length})</TabsTrigger>
              <TabsTrigger value="memo" data-testid="tab-memo"><Inbox className="w-3 h-3 mr-1" />Memo ({memos.length})</TabsTrigger>
              <TabsTrigger value="chat" data-testid="tab-chat"><MessageCircle className="w-3 h-3 mr-1" />Chat</TabsTrigger>
              <TabsTrigger value="note" data-testid="tab-notes">Note ({notes.length})</TabsTrigger>
              <TabsTrigger value="fatturazione" data-testid="tab-billing"><Receipt className="w-3 h-3 mr-1" />Fatturazione</TabsTrigger>
              <TabsTrigger value="attivita" data-testid="tab-activity">Cronologia</TabsTrigger>
            </TabsList>

            <TabsContent value="riepilogo">
              <Card className="p-5 space-y-4">
                <div>
                  <div className="font-heading font-semibold text-navy-900 mb-2">Descrizione</div>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{p.description || "Nessuna descrizione."}</p>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-heading font-semibold text-navy-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Riepilogo AI</div>
                    <Button size="sm" variant="outline" onClick={generateSummary} disabled={summaryLoading} data-testid="ai-summary-btn">
                      {summaryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Genera con Claude"}
                    </Button>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-line min-h-[80px] bg-amber-50/40 p-3 rounded">
                    {summary || <span className="text-slate-400">Genera un riepilogo intelligente con citazione delle fonti dal contesto del progetto.</span>}
                  </div>
                </div>
                <div className="border-t pt-4">
                  <div className="font-heading font-semibold text-navy-900 mb-2">Persone coinvolte</div>
                  <div className="flex flex-wrap gap-2">
                    {p.assignees_detail?.map(u => (
                      <Badge key={u.id} variant="outline" className="border-navy-200 text-navy-700 bg-navy-50">
                        {u.full_name} <span className="ml-1.5 text-[10px] text-slate-500 capitalize">{u.role?.replace("_"," ")}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="task">
              <Card className="p-5 space-y-3">
                <div className="flex gap-2">
                  <Input value={newTask} onChange={(e)=>setNewTask(e.target.value)} placeholder="Aggiungi nuovo task..." onKeyDown={(e)=>e.key==="Enter"&&addTask()} data-testid="new-task-input"/>
                  <Button onClick={addTask} className="bg-navy-800 hover:bg-navy-900" data-testid="add-task-btn"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-1.5">
                  {tasks.map(t => (
                    <div key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-md group">
                      <Checkbox checked={t.status === "completata"} onCheckedChange={()=>toggleTask(t)} />
                      <span className={`flex-1 text-sm ${t.status === "completata" ? "line-through text-slate-400" : "text-navy-900"}`}>{t.title}</span>
                      {t.due_date && <span className="text-xs text-slate-500">{t.due_date}</span>}
                      <button onClick={()=>removeTask(t.id)} className="opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {tasks.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessun task</div>}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="documenti">
              <Card className="p-5">
                <div className="space-y-2">
                  {docs.map(d => (
                    <div key={d.id} className="flex items-center gap-3 p-3 border border-slate-100 rounded-md">
                      <FileText className="w-5 h-5 text-navy-600" />
                      <div className="flex-1"><div className="text-sm font-medium text-navy-900">{d.name}</div><div className="text-xs text-slate-500">{d.doc_type} · v{d.version_number}</div></div>
                      <a className="text-xs text-navy-600 hover:underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">Scarica</a>
                    </div>
                  ))}
                  {docs.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessun documento collegato</div>}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="memo">
              <Card className="p-5 space-y-2">
                {memos.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessun memo collegato a questa pratica</div>}
                {memos.map(m => (
                  <div key={m.id} className="p-3 border border-slate-200 rounded-md" data-testid={`pmemo-${m.id}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-medium text-navy-900">{m.subject}</span>
                      <Badge className={`${prioColors[m.priority]} border-0 text-[10px]`}>{m.priority}</Badge>
                      <Badge variant="outline" className="text-[10px]">{typeLabel[m.memo_type]}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{m.my_status?.replace("_"," ")}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">Da {m.sender_name} · {new Date(m.created_at).toLocaleString("it-IT")}</div>
                    <div className="text-sm text-slate-700 mt-2 whitespace-pre-line">{m.body}</div>
                  </div>
                ))}
              </Card>
            </TabsContent>

            <TabsContent value="chat">
              <ProjectChat projectId={id} />
            </TabsContent>

            <TabsContent value="note">
              <Card className="p-5 space-y-3">
                <Textarea rows={3} value={newNote} onChange={(e)=>setNewNote(e.target.value)} placeholder="Scrivi una nota interna..." data-testid="new-note-input"/>
                <Button onClick={addNote} className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="add-note-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi nota</Button>
                <div className="space-y-3 mt-4">
                  {notes.map(n => (
                    <div key={n.id} className="p-3 border-l-2 border-navy-500 bg-slate-50 rounded">
                      <div className="text-xs text-slate-500 mb-1">{n.author_name} · {new Date(n.created_at).toLocaleString("it-IT")}</div>
                      <div className="text-sm text-slate-800 whitespace-pre-line">{n.content}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="fatturazione">
              <Fatturazione projectId={id} />
            </TabsContent>

            <TabsContent value="attivita">
              <Card className="p-5">
                <div className="space-y-2">
                  {activity.map(a => (
                    <div key={a.id} className="text-xs text-slate-600 flex items-center gap-3 py-1.5 border-b border-slate-100">
                      <span className="text-slate-400 w-36">{new Date(a.performed_at).toLocaleString("it-IT")}</span>
                      <span className="text-navy-900 font-medium">{a.user_name}</span>
                      <span>{a.action}</span>
                      <span className="text-slate-500 italic">{a.entity_type}</span>
                    </div>
                  ))}
                  {activity.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessuna attività</div>}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-1 space-y-5">
          <ProjectAISidebar projectId={id} />
          <SimilarPracticesPanel projectId={id} />
        </div>
      </div>
    </div>
  );
}
